<template>
  <v-data-table v-bind="dataTableOptions" :items="nodes" :item-key="itemKey" class="elevation-0 content">
    <template v-slot:top>
      <slot name="top" v-bind="{ nodes, headers }"></slot>
    </template>
    <template v-slot:item="{ item: node, headers }">
      <slot name="item" v-bind="{ node, headers }">
        <tr v-show="showNode(node)" :class="{ 'v-data-table__selected': node._selected }" @click="onClickRow($event, node)" @click.right.prevent="onClickRowRight($event, node)">
          <td v-for="(header, index) in headers" :key="index" :class="`text-${header.align ? header.align : 'start'}`">
            <span v-if="header.expandable">
              <span :style="nodeStyle(node)"></span>
              <expandable-btn :node="node" @click="toggleNode"></expandable-btn>
            </span>
            <slot :name="`item.${header.value}`" v-bind="{ item: node, headers, value: node[header.value] }">
              {{ node[header.value] }}
            </slot>
          </td>
        </tr>
      </slot>
    </template>
    <template v-slot:footer>
      <slot name="footer" v-bind="{ nodes, headers }"></slot>
    </template>
  </v-data-table>
</template>

<script>
/**
 * 속성 중 Underscore(_) prefix가 붙은 것은 내부적으로 새롭게 추가한 속성으로
 * 사용자가 props으로 넘겨준 데이터의 속성 이름과 중복을 피하고
 * 또 Tree의 속성인지 사용자 데이터 속성인지 구분하기 위한 것임.
 *
 * TreeDataTable에 적용되어야하는 VDataTable의 속성들(fixedHeader, dense, disablePagination)은
 * 기본적으로 props의 정의하여 VDataTable에 적용될 수 있도록 했다.
 * 더불어 click:row, contextmenu:row 의 이벤트도 대응될 수 있도록 구현되어있다.
 * 추후 더 많은 속성 및 이벤트가 필요할 경우 그때 그때 추가하도록한다.
 */
import { flattenDeep, merge, cloneDeep } from 'lodash';
import ExpandableBtn from './ExpandableBtn';

export default {
  name: 'TreeDataTable',
  components: {
    ExpandableBtn,
  },
  props: {
    // VDataTable options
    headers: Array,
    items: [Array, Object],
    itemKey: String,
    height: [Number, String],
    fixedHeader: Boolean,
    dense: Boolean,
    disablePagination: Boolean,
    hideDefaultFooter: Boolean,
    singleSelect: Boolean,
    loading: Boolean,
    itemsPerPage: [Number, String],

    // TreeDataTable options
    openAll: Boolean,
  },
  data() {
    return {
      nodes: [],
    };
  },
  computed: {
    dataTableOptions() {
      const options = merge({}, this.$props);
      // delete options.headers;
      delete options.openAll;
      return options;
    },
  },
  watch: {
    items: {
      immediate: true,
      handler(value) {
        if (Array.isArray(value)) {
          this.nodes = this.flattenNodes(cloneDeep(value));
        }
      },
    },
    openAll: {
      immediate: true,
      handler(value) {
        this.expandAll(value);
      },
    },
  },
  methods: {
    flattenNodes(nodes) {
      let flattenNodes = [];
      nodes.forEach(node => {
        node._depth = 1;
        this.$set(node, '_showed', false);
        this.$set(node, '_expanded', false);
        flattenNodes.push(node);
        flattenNodes.push(this.flattenNode(node));
      });
      return flattenDeep(flattenNodes);
    },
    flattenNode(node, children = []) {
      if (node.children) {
        children = children.concat(node.children);
        node.children.forEach(child => {
          child._parentNode = node;
          child._depth = node._depth + 1;
          this.$set(child, '_showed', false);
          this.$set(child, '_expanded', false);
          children.splice(children.indexOf(child) + 1, 0, this.flattenNode(child));
        });
      }
      return flattenDeep(children);
    },
    showNode(node) {
      if (!node._parentNode) {
        return true;
      } else if (node._parentNode._expanded) {
        return true;
      } else {
        return false;
      }
    },
    nodeStyle(node) {
      return {
        paddingLeft: `${node._depth > 1 ? node._depth * 15 : 0}px`,
      };
    },
    expandAll(expanded) {
      this.nodes.forEach(node => {
        node._showed = expanded;
        node._expanded = expanded;
        if (expanded && !node._opened) {
          node._opened = true;
        }
      });
    },
    toggleNode(node, { loading }) {
      node._showed = !node._expanded;
      node._expanded = !node._expanded;
      if (node._expanded) {
        this.expandChildren(node);
      } else {
        this.collapseChildren(node);
      }

      const vm = this;
      this.$emit('click:expand', node, {
        expanded: node._expanded,
        loading,
        add(nodes) {
          const addNode = (newNode, parentNode) => {
            vm.$set(newNode, '_showed', false);
            vm.$set(newNode, '_expanded', false);
            newNode._depth = parentNode._depth + 1;
            newNode._parentNode = parentNode;
            parentNode.children = parentNode.children ? parentNode.children.concat(newNode) : [newNode];
            // parentNode._showed = true;

            const index = vm.nodes.indexOf(parentNode);
            ///////////////////////////////////////////////////////////////////////////////////////////////
            // 다음과 같이 splice를 하면 추가되는 자식이 여러개일 때, 매달리는 자식의 순서가 뒤집히게 된다.
            vm.nodes.splice(index + 1, 0, newNode);
            ///////////////////////////////////////////////////////////////////////////////////////////////
            // 그래서 이렇게 바꿈.2020.12.28.KWShin.
            // const childLength = parentNode.children.length;
            // vm.nodes.splice(index + childLength, 0, newNode);
            // 그런데, 바꾸었더니 User history/Program history에서 다양한 현상이 발생한다.
            // - 자식이 추가되는 위치의 문제, 자식이 원래 있는 경우와 expand시 읽어 온 경우의 문제 등...
            // 일단 원복하고 expand시 추가되는 children 배열은 reverse()하여 추가함.
            // 뭔가 수정이 필요할 듯... 2021.01.11.KWShin.
            ///////////////////////////////////////////////////////////////////////////////////////////////
            if (newNode.children && newNode.children.length > 0) {
              for (const child of newNode.children) {
                addNode(child, newNode);
              }
            }
          };

          const newNodes = Array.isArray(nodes) ? nodes : [nodes];
          for (const newNode of newNodes) {
            addNode(newNode, node);
          }

          // for (const newNode of newNodes) {
          //   vm.$set(newNode, '_showed', false);
          //   vm.$set(newNode, '_expanded', false);
          //   newNode._depth = node._depth + 1;
          //   newNode._parentNode = node;

          //   // 신규 노드(newNode)를 클릭한 노드(node)의 자식으로 등록
          //   node.children = node.children ? node.children.concat(newNode) : [newNode];

          //   // 자식으로 등록된 노드가 있으므로 node._showed를 true로 설정
          //   // TODO. 상위 노드에서 트리를 접었다 펼칠 때 접기전 형태 그대로 펼치기 위해
          //   //       _expanded 속성 외 _showed라는 추가적인 상태 값을 사용하는데
          //   //       두 개의 상태를 기준으로 하나의 행위(접고 펼치는)를 정의하려니..
          //   //       복잡하고 사실 상태 추적도 잘 안됨... ㅠㅠ 시간 날 때 천천히 고쳐야 할 듯..
          //   //       근데 정말 리펙토링할 수 있을까?? ㅋ
          //   node._showed = true;
          //   const index = vm.nodes.indexOf(node);
          //   vm.nodes.splice(index + 1, 0, newNode);
          // }
        },
        remove(node) {
          const index = vm.nodes.indexOf(node);
          vm.nodes.splice(index, 1);
        },
        update(node) {
          const index = vm.nodes.indexOf(node);
          vm.nodes.splice(index, 1, node);
        },
        hasChildren(node) {
          for (const child of node.children) {
            const index = vm.nodes.indexOf(child);
            if (index > -1) {
              return true;
            }
          }
          return false;
        },
      });

      // 컴포넌트를 사용하는 곳에서 트리를 오픈할 때 이미 한 번 오픈한 Node인지 식별하기 위한 설정 값
      // 보통 비동기 통신에서 한 번만 데이터를 가져오기 위해 사용됨
      if (!node._opened && node._expanded) {
        node._opened = true;
      }
    },
    expandChildren(node) {
      const vm = this;
      node._expanded = node._parentNode ? node._showed && node._parentNode._expanded : node._showed;
      if (node.children) {
        node.children.forEach(child => {
          vm.expandChildren(child);
        });
      }
    },
    collapseChildren(node) {
      const vm = this;
      node._expanded = false;
      if (node.children) {
        node.children.forEach(child => {
          vm.collapseChildren(child);
        });
      }
    },
    onClickRow(e, node) {
      const tagName = e.target.tagName;
      if (tagName === 'I' || tagName === 'BUTTON') {
        return;
      }
      this.emit('click:row', node, node);
    },
    onClickRowRight(e, node) {
      this.emit('contextmenu:row', node, e);
    },
    emit(name, node, arg) {
      const vm = this;
      this.$emit(name, arg, {
        headers: this.headers,
        expanded: node._expanded,
        selected: node._selected,
        node: node,
        select(value) {
          vm.$set(node, '_selected', value);
          if (value && vm.singleSelect) {
            const selectedIndex = vm.nodes.indexOf(node);
            vm.nodes.forEach((n, index) => {
              if (index !== selectedIndex) {
                if (n._selected) {
                  n._selected = false;
                  return false;
                }
              }
            });
          }
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped></style>
